import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ArrowLeft, Pencil, Trash2, Plus, Send, CheckCheck, Archive, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { can, PRIORITY, TASK_STATUS, initials } from "@/lib/colors";
import { fmt } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { Pill, Empty } from "@/components/common";
import { ProjectDialog, PROJECT_STATUS } from "@/components/projects/ProjectDialog";
import { ProjectChecklist } from "@/components/projects/ProjectChecklist";
import { CompleteProjectDialog } from "@/components/projects/CompleteProjectDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Notes = ({ project, user, onChanged }) => {
  const [text, setText] = useState("");
  const add = async () => {
    try {
      await api.post(`/projects/${project.id}/notes`, { text });
      setText("");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/projects/${project.id}/notes/${n.id}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section>
      <h2 className="text-base md:text-lg font-bold text-slate-800 mb-3">Notes</h2>
      {!project.archived && <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-3">
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" data-testid="project-note-input" />
        <div className="flex justify-end mt-2"><Button className="rounded-full" size="sm" disabled={!text.trim()} onClick={add} data-testid="project-note-add-btn"><Send className="w-4 h-4 mr-1.5" /> Add note</Button></div>
      </div>}
      <div className="space-y-2" data-testid="project-notes-list">
        {project.notes.map((n) => (
          <div key={n.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex gap-3" data-testid={`project-note-${n.id}`}>
            <span className="w-8 h-8 rounded-full bg-[#EAF2F2] text-[#24474A] grid place-items-center text-xs font-bold shrink-0">{initials(n.author_name)}</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500"><span className="font-bold text-slate-700">{n.author_name}</span> · {formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{n.text}</p>
            </div>
            {!project.archived && (n.author_id === user.id || can(user, "manage_projects")) && (
              <Button size="icon" variant="ghost" onClick={() => remove(n)} data-testid={`project-note-delete-${n.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
            )}
          </div>
        ))}
        {project.notes.length === 0 && <Empty>No notes yet.</Empty>}
      </div>
    </section>
  );
};

const TaskRow = ({ t }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`project-task-${t.id}`}>
    <span className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY[t.priority].dot}`} />
    <div className="flex-1 min-w-0">
      <div className={`font-bold text-sm text-slate-800 truncate ${t.status === "done" ? "line-through opacity-60" : ""}`}>{t.title}</div>
      <div className="text-xs text-slate-500">{t.assignee_name} · {t.due ? `due ${fmt(t.due, "d MMM, HH:mm")}` : "no due date"}</div>
    </div>
    <Pill cls={TASK_STATUS[t.status].cls}>{TASK_STATUS[t.status].label}</Pill>
  </div>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch (e) {
      toast.error(errMsg(e));
      navigate("/projects");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    if (!window.confirm("Delete this project? Its notes are deleted and its tasks are kept without a project.")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    navigate("/projects");
  };

  if (!project) return null;
  const manage = can(user, "manage_projects");
  const loc = project.kind === "location";
  const pill = project.archived ? { label: "Archived", cls: "bg-slate-100 text-slate-500 border-slate-200" } : loc ? { label: "Location", cls: "bg-[#EAF2F2] text-[#24474A] border-[#24474A]/20" } : PROJECT_STATUS[project.status];
  return (
    <div>
      <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#24474A] mb-4" data-testid="project-back-link"><ArrowLeft className="w-4 h-4" /> All projects/locations</Link>
      {project.archived && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 mb-4 text-sm text-slate-600" data-testid="project-archived-banner">
          <Archive className="w-4 h-4" /> Completed and archived{project.archived_at && ` on ${fmt(project.archived_at, "d MMM yyyy")}`}. Read-only.
          {project.location_id && project.location_name && <Link to={`/projects/${project.location_id}`} className="font-bold text-[#24474A] inline-flex items-center gap-1" data-testid="project-location-link"><MapPin className="w-4 h-4" /> Go to location {project.location_name}</Link>}
        </div>
      )}
      {project.archived_from && project.archived_from_name && (
        <p className="text-xs text-slate-500 mb-3" data-testid="location-origin">Created from completed project <Link to={`/projects/${project.archived_from}`} className="font-bold text-[#24474A]">{project.archived_from_name}</Link></p>
      )}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 fade-up">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            {loc && <MapPin className="w-8 h-8 text-[#24474A]" />}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#24474A]" data-testid="project-title">{project.name}</h1>
            <Pill cls={pill.cls} testid="project-status">{pill.label}</Pill>
          </div>
          {project.description && <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-2xl whitespace-pre-wrap">{project.description}</p>}
          <p className="text-xs text-slate-400 mt-2">Created by {project.created_by_name || "—"}{project.template !== "none" && project.opening_date && <> · <span className="font-bold text-[#24474A]" data-testid="project-opening-date">Opening {fmt(project.opening_date, "EEE d MMM yyyy")}</span></>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!project.archived && <Button variant="outline" className="rounded-full" onClick={() => setAdding(true)} data-testid="project-add-task-btn"><Plus className="w-4 h-4 mr-1.5" /> Add task</Button>}
          {manage && !project.archived && <Button variant="outline" className="rounded-full" onClick={() => setEditing(true)} data-testid="project-edit-btn"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>}
          {manage && !project.archived && !loc && <Button className="rounded-full" onClick={() => setCompleting(true)} data-testid="project-complete-btn"><CheckCheck className="w-4 h-4 mr-1.5" /> Complete project</Button>}
          {manage && <Button variant="ghost" size="icon" onClick={remove} data-testid="project-delete-btn"><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
        </div>
      </div>
      {project.template !== "none" && !loc && <ProjectChecklist project={project} onChanged={load} readOnly={project.archived} />}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
        <Notes project={project} user={user} onChanged={load} />
        <section>
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-3">Tasks <span className="text-slate-400">({project.tasks.length})</span></h2>
          <div className="rounded-2xl border border-slate-200 bg-white" data-testid="project-tasks-list">
            {project.tasks.map((t) => <TaskRow key={t.id} t={t} />)}
            {project.tasks.length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center">No tasks linked yet.</div>}
          </div>
        </section>
      </div>
      <ProjectDialog open={editing} project={project} onClose={() => setEditing(false)} onSaved={load} />
      <TaskDialog open={adding} presetProject={project.id} onClose={() => setAdding(false)} onSaved={load} />
      {completing && <CompleteProjectDialog open={completing} project={project} onClose={() => setCompleting(false)} onDone={(locId) => { setCompleting(false); if (locId) navigate(`/projects/${locId}`); else load(); }} />}
    </div>
  );
}
